import { lazy, Suspense } from 'react';
import { Redirect, RouteChildrenProps } from 'react-router';
import { Box, LinearProgress } from '@rossum/ui/material';
import { HelmetComponent } from '../../../routes/HelmetComponent';
import { extractMetabaseAuthErrorMessage } from '../core/api';
import { useReportingFlags } from '../core/hooks';
import { useGetMetabaseSessionToken } from './hooks/useGetMetabaseSessionToken';

const LazyLoadedReporting = lazy(() => import('./MetabaseReportingRoutes'));

export const ReportingRoutes = (props: RouteChildrenProps) => {
  const { match } = props;

  const {
    isReportingEnabled,
    isLoading: isAccessLoading,
    isError: isAccessError,
  } = useReportingFlags();

  const { isSuccess, isError, error } = useGetMetabaseSessionToken({
    enabled: isReportingEnabled,
  });

  if (!match) return null;

  if (isAccessLoading) {
    return <LinearProgress />;
  }

  if (isAccessError || !isReportingEnabled) {
    return <Redirect to="/error?reason=not-found" />;
  }

  if (isError) {
    return (
      <Redirect
        to={{
          pathname: '/error',
          search: '?reason=metabase-auth-error',
          state: { customMessage: extractMetabaseAuthErrorMessage(error) },
        }}
      />
    );
  }

  return (
    <Suspense fallback={<LinearProgress />}>
      <Box
        sx={{
          height: 1,
          '[data-testid="dashboard-parameter-sidebar"]': {
            pb: '55px',
          },
          '[role="grid"]': { boxSizing: 'border-box' },
        }}
      >
        <HelmetComponent translationKey="features.routes.pageTitles.insights" />
        <LazyLoadedReporting isSuccess={isSuccess} match={match} />
      </Box>
    </Suspense>
  );
};
